import { api, API_BASE_URL  } from '../../utils/api'

// Use the existing API instance
const videoApi = api

videoApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * Create a new room (legacy method)
 * @returns {Promise<Object>} Room data with roomId and url
 */
export async function createRoom() {
  try {
    const response = await videoApi.post('/api/video/create-room')
    return response
  } catch (error) {
    console.error('[Room Service] Create room error:', error)
    throw error
  }
}

/**
 * Get room details by ID
 * @param {string} roomId - Room ID
 * @returns {Promise<Object>} Room details
 */
export async function getRoomDetails(roomId) {
  try {
    const response = await videoApi.get(`/api/video/room/${roomId}`)
    return response.data
  } catch (error) {
    console.error('[Room Service] Get room details error:', error)
    throw error
  }
}

/**
 * List interviews for company user
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Filter by status (active, completed, etc.)
 * @param {string} filters.candidateId - Filter by candidate
 * @param {string} filters.jobId - Filter by job
 * @param {number} filters.limit - Number of results
 * @param {number} filters.offset - Pagination offset
 * @returns {Promise<Object>} List of interviews
 */
export async function listCompanyInterviews(filters = {}) {
  try {
    const response = await videoApi.get('/api/video/room/list', {
      params: filters
    })
    return response.data
  } catch (error) {
    console.error('[Room Service] List company interviews error:', error)
    throw error
  }
}

/**
 * List interviews for candidate
 * @param {Object} filters - Filter options
 * @returns {Promise<Object>} List of accessible interviews
 */
export async function listCandidateInterviews(filters = {}) {
  try {
    const response = await videoApi.get('/api/video/room/candidate/list', {
      params: filters
    })
    return response.data
  } catch (error) {
    console.error('[Room Service] List candidate interviews error:', error)
    throw error
  }
}

/**
 * Get transcript for a room
 * @param {string} roomId - Room ID
 * @returns {Promise<Object>} Transcript data
 */
export async function getTranscript(roomId) {
  try {
    const response = await videoApi.get(`/api/video/room/${roomId}/transcript`)
    return response.data
  } catch (error) {
    console.error('[Room Service] Get transcript error:', error)
    throw error
  }
}

/**
 * Update transcript visibility for candidate
 * @param {string} roomId - Room ID
 * @param {boolean} visible - Whether transcript should be visible to candidate
 * @returns {Promise<Object>} Updated transcript
 */
export async function updateTranscriptVisibility(roomId, visible) {
  try {
    const response = await videoApi.patch(`/api/video/room/${roomId}/transcript/visibility`, {
      visibleToCandidate: visible
    })
    return response.data
  } catch (error) {
    console.error('[Room Service] Update transcript visibility error:', error)
    throw error
  }
}

/**
 * Get assessment for a room
 * @param {string} roomId - Room ID
 * @returns {Promise<Object>} Assessment data
 */
export async function getAssessment(roomId) {
  try {
    const response = await videoApi.get(`/api/video/room/${roomId}/assessment`)
    return response.data
  } catch (error) {
    console.error('[Room Service] Get assessment error:', error)
    throw error
  }
}

/**
 * Create or update assessment requirements
 * @param {string} roomId - Room ID
 * @param {Object} requirements - Assessment requirements
 * @param {Array<string>} requirements.criteria - List of criteria to evaluate
 * @param {Array<string>} requirements.keywords - Keywords to focus on
 * @param {string} requirements.role - Role being assessed
 * @param {string} requirements.experienceLevel - Experience level (junior, mid, senior)
 * @returns {Promise<Object>} Updated assessment
 */
export async function updateAssessmentRequirements(roomId, requirements) {
  try {
    const response = await videoApi.post(`/api/video/room/${roomId}/assessment/requirements`, requirements)
    return response.data
  } catch (error) {
    console.error('[Room Service] Update assessment requirements error:', error)
    throw error
  }
}

/**
 * Update assessment visibility for candidate
 * @param {string} roomId - Room ID
 * @param {boolean} visible - Whether assessment should be visible to candidate
 * @returns {Promise<Object>} Updated assessment
 */
export async function updateAssessmentVisibility(roomId, visible) {
  try {
    const response = await videoApi.patch(`/api/video/room/${roomId}/assessment/visibility`, {
      visibleToCandidate: visible
    })
    return response.data
  } catch (error) {
    console.error('[Room Service] Update assessment visibility error:', error)
    throw error
  }
}

/**
 * Add review to assessment
 * @param {string} roomId - Room ID
 * @param {Object} review - Review data
 * @param {string} review.reviewerId - Reviewer user ID
 * @param {string} review.reviewerName - Reviewer name
 * @param {number} review.rating - Rating (1-5)
 * @param {string} review.comments - Review comments
 * @param {string} review.decision - Decision (hire, reject, next_round, hold)
 * @returns {Promise<Object>} Updated assessment
 */
export async function addAssessmentReview(roomId, review) {
  try {
    const response = await videoApi.post(`/api/video/room/${roomId}/assessment/review`, review)
    return response.data
  } catch (error) {
    console.error('[Room Service] Add assessment review error:', error)
    throw error
  }
}

/**
 * Get list of all recordings (legacy method)
 * @returns {Promise<Array>} List of recordings
 */
export async function getRecordings() {
  try {
    const response = await videoApi.get('/api/video/call/recordings')
    return response.recordings || []
  } catch (error) {
    console.error('[Room Service] Get recordings error:', error)
    throw error
  }
}

/**
 * Get recordings for a specific room
 * @param {string} roomId - Room ID
 * @returns {Promise<Object>} Recording data
 */
export async function getRoomRecordings(roomId) {
  try {
    const response = await videoApi.get(`/api/video/room/${roomId}/recordings`)
    return response.data
  } catch (error) {
    console.error('[Room Service] Get room recordings error:', error)
    throw error
  }
}

/**
 * Get recording file URL (legacy method)
 * @param {string} roomId - Room ID
 * @param {string} filename - Recording filename
 * @returns {string} Recording URL
 */
export function getRecordingUrl(roomId, filename) {
  return `/video/call/recordings/${roomId}/${filename}`
}

/**
 * Grant candidate access to view interview materials
 * @param {string} roomId - Room ID
 * @param {Object} access - Access settings
 * @param {boolean} access.transcript - Grant transcript access
 * @param {boolean} access.assessment - Grant assessment access
 * @param {boolean} access.recording - Grant recording access
 * @returns {Promise<Object>} Updated room
 */
export async function grantCandidateAccess(roomId, access) {
  try {
    const response = await videoApi.post(`/api/video/room/${roomId}/access/grant`, access)
    return response.data
  } catch (error) {
    console.error('[Room Service] Grant candidate access error:', error)
    throw error
  }
}

/**
 * Revoke candidate access to interview materials
 * @param {string} roomId - Room ID
 * @param {Object} access - Access settings to revoke
 * @param {boolean} access.transcript - Revoke transcript access
 * @param {boolean} access.assessment - Revoke assessment access
 * @param {boolean} access.recording - Revoke recording access
 * @returns {Promise<Object>} Updated room
 */
export async function revokeCandidateAccess(roomId, access) {
  try {
    const response = await videoApi.post(`/api/video/room/${roomId}/access/revoke`, access)
    return response.data
  } catch (error) {
    console.error('[Room Service] Revoke candidate access error:', error)
    throw error
  }
}

/**
 * Share interview with other company users
 * @param {string} roomId - Room ID
 * @param {Array<string>} userIds - User IDs to share with
 * @returns {Promise<Object>} Updated room
 */
export async function shareInterview(roomId, userIds) {
  try {
    const response = await videoApi.post(`/api/video/room/${roomId}/share`, {
      userIds
    })
    return response.data
  } catch (error) {
    console.error('[Room Service] Share interview error:', error)
    throw error
  }
}

/**
 * Unshare interview with company users
 * @param {string} roomId - Room ID
 * @param {Array<string>} userIds - User IDs to remove access
 * @returns {Promise<Object>} Updated room
 */
export async function unshareInterview(roomId, userIds) {
  try {
    const response = await videoApi.post(`/api/video/room/${roomId}/unshare`, {
      userIds
    })
    return response.data
  } catch (error) {
    console.error('[Room Service] Unshare interview error:', error)
    throw error
  }
}

/**
 * Archive a completed interview
 * @param {string} roomId - Room ID
 * @returns {Promise<Object>} Updated room
 */
export async function archiveInterview(roomId) {
  try {
    const response = await videoApi.post(`/api/video/room/${roomId}/archive`)
    return response.data
  } catch (error) {
    console.error('[Room Service] Archive interview error:', error)
    throw error
  }
}

/**
 * Delete a room (admin only)
 * @param {string} roomId - Room ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteRoom(roomId) {
  try {
    const response = await videoApi.delete(`/api/video/room/${roomId}`)
    return response.data
  } catch (error) {
    console.error('[Room Service] Delete room error:', error)
    throw error
  }
}

/**
 * Get room statistics
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date for stats
 * @param {string} filters.endDate - End date for stats
 * @param {string} filters.jobId - Filter by job
 * @returns {Promise<Object>} Room statistics
 */
export async function getRoomStats(filters = {}) {
  try {
    const response = await videoApi.get('/api/video/room/stats', {
      params: filters
    })
    return response.data
  } catch (error) {
    console.error('[Room Service] Get room stats error:', error)
    throw error
  }
}

export default {
  // Legacy methods
  createRoom,
  getRecordings,
  getRecordingUrl,

  // Room management
  getRoomDetails,
  listCompanyInterviews,
  listCandidateInterviews,

  // Transcripts
  getTranscript,
  updateTranscriptVisibility,

  // Assessments
  getAssessment,
  updateAssessmentRequirements,
  updateAssessmentVisibility,
  addAssessmentReview,

  // Recordings
  getRoomRecordings,

  // Access control
  grantCandidateAccess,
  revokeCandidateAccess,
  shareInterview,
  unshareInterview,

  // Room operations
  archiveInterview,
  deleteRoom,
  getRoomStats
}
